import { UserState } from '../data/UserState';
import { StorageService } from '../storage/StorageService';

export class Util {
	public static isString(value: string): boolean {
		// eslint-disable-next-line
		if (typeof value === 'string' || (value as any) instanceof String) {
			return true;
		}
		return false;
	}

	public static isObjectEmpty(value: object): boolean {
		if (value && Object.keys(value).length === 0) {
			return true;
		}
		return false;
	}

	public static stringIsEmpty(value: string): boolean {
		if (this.isString(value)) {
			return value.length === 0 || !value.trim();
		}
		return false;
	}

	public static getTrimmedLowercaseString(value: string) {
		if (this.isString(value)) {
			return value.trim().toLowerCase();
		}
		return null;
	}

	public static getTrimmedUppercaseString(value: string) {
		if (this.isString(value)) {
			return value.trim().toUpperCase();
		}
		return null;
	}

	public static getCombinedKey(value1: string, value2: string): string {
		return `${value1.trim().toLowerCase()}/${value2.trim().toLowerCase()}`;
	}

	public static stringToTitleCase(value: string): string {
		if (this.isString(value) && !this.stringIsEmpty(value)) {
			return value.replace(/\w\S*/g, (sIntermediate) => {
				return sIntermediate.charAt(0).toUpperCase() + sIntermediate.substring(1).toLowerCase();
			});
		}
		return value;
	}

	public static isQualtricsSurveyPopupOpen(): boolean {
		const qualtricsPopup = document.getElementsByClassName('QSIPopOver');
		const qualtricsPopupResponsive = document.getElementsByClassName('QSIWebResponsive');
		if (qualtricsPopup?.length || qualtricsPopupResponsive?.length || this.isQualtricsIFrameAttached()) {
			return true;
		}
		return false;
	}

	public static isQualtricsIFrameAttached(): boolean {
		const currentIFrames = document.getElementsByTagName('iframe');
		for (const iFrame of currentIFrames) {
			const iFrameName = iFrame.getAttribute('name');
			if (iFrameName && iFrameName.includes('survey-iframe-')) {
				return true;
			}
		}
		return false;
	}

	public static readUserState(storageService: StorageService): UserState | undefined {
		return storageService.readUserState();
	}

	public static randomInt(start: number, end: number) {
		start = Math.ceil(start);
		end = Math.floor(end);
		return Math.floor(Math.random() * (end - start)) + start;
	}

	public static isLocalStorageAvailable() {
		return !!localStorage;
	}

	public static ensureGlobalContext(firstLevel?: string, secondLevel?: string): any {
		if (!window.sap) {
			window.sap = {};
		}
		if (firstLevel) {
			if (!window.sap[firstLevel]) {
				window.sap[firstLevel] = {};
			}
			const createdFirstLevel = window.sap[firstLevel];
			if (secondLevel) {
				if (!createdFirstLevel[secondLevel]) {
					createdFirstLevel[secondLevel] = {};
				}
				return createdFirstLevel[secondLevel];
			}
			return createdFirstLevel;
		}
		return window.sap;
	}

	public static getGlobalContextValue(firstLevel?: string, secondLevel?: string): any | undefined {
		if (window.sap) {
			if (firstLevel && !secondLevel) {
				return window.sap[firstLevel] || undefined;
			}
			if (firstLevel && secondLevel && window.sap[firstLevel]) {
				return window.sap[firstLevel][secondLevel] || undefined;
			}
		}
		return undefined;
	}

	public static getWindowSearchLocation(): string {
		return encodeURIComponent(window.location.search);
	}

	public static isUrlParamDefined(urlParamKey: string): boolean {
		const queryString = this.getWindowSearchLocation();
		if (queryString) {
			const urlParams = new URLSearchParams(decodeURIComponent(queryString));
			if (urlParams && urlParams.has(urlParamKey)) {
				return true;
			}
		}
		return false;
	}

	public static isUrlParamActive(urlParamKey: string, expectedValue: string): boolean {
		const queryString = Util.getWindowSearchLocation();
		if (queryString) {
			const urlParams = new URLSearchParams(decodeURIComponent(queryString));
			if (urlParams && urlParams.has(urlParamKey)) {
				const urlParamState = urlParams.get(urlParamKey);
				if (urlParamState && urlParamState.trim().toLocaleLowerCase() === expectedValue) {
					return true;
				}
			}
		}
		return false;
	}

	public static containsUrlParam(urlParamKey: string, expectedValue: string): boolean {
		const queryString = Util.getWindowSearchLocation();
		if (queryString) {
			const urlParams = new URLSearchParams(decodeURIComponent(queryString));
			if (urlParams && urlParams.has(urlParamKey)) {
				const urlParamState = urlParams.get(urlParamKey);
				if (urlParamState) {
					const paramValues = urlParamState.trim().split(',');
					return paramValues.findIndex(
						(paramValue) => paramValue.trim().toLocaleLowerCase() === expectedValue.trim().toLocaleLowerCase()
					) === -1
						? false
						: true;
				}
			}
		}
		return false;
	}

	public static getQueryParamValue(urlParamKey: string): string | null {
		if (urlParamKey) {
			const queryString = Util.getWindowSearchLocation();
			if (queryString) {
				const urlParams = new URLSearchParams(decodeURIComponent(queryString));
				if (urlParams && urlParams.has(urlParamKey)) {
					return urlParams.get(urlParamKey);
				}
			}
		}
		return null;
	}

	public static prepareBaseUrl(baseUrl: string): string {
		return baseUrl.trim().endsWith('/') ? baseUrl.trim() : `${baseUrl.trim()}/`;
	}
}
